import { ChangeEvent, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import InfoOutlineIcon from "@mui/icons-material/InfoOutline";
import BarChartIcon from "@mui/icons-material/BarChart";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import { FormikContextType } from "formik";
import { useNavigate } from "react-router-dom";
import CapabilitiesDialog from "./CapabilitiesDialog";
import HostMediaFolder from "./HostMediaFolder";
import type { RuntimeConfig } from "../pages/ConfigPage";
import { useAuth } from "../context/AuthContext";

type ServerFormProps = {
  formik: FormikContextType<RuntimeConfig>;
  defaultServerName: string;
  canRestartServer: boolean;
  isDesktopShell: boolean;
  onRequestRestart: () => void;
  restartStatus: { pending: boolean; error: string | null };
};

const ServerForm = ({
  formik,
  defaultServerName,
  canRestartServer,
  isDesktopShell,
  onRequestRestart,
  restartStatus,
}: ServerFormProps) => {
  const navigate = useNavigate();
  const { authorizedFetch } = useAuth();
  const [capabilitiesDialogOpen, setCapabilitiesDialogOpen] = useState(false);
  const [hostMediaDialogOpen, setHostMediaDialogOpen] = useState(false);
  const [selectedHostMediaPath, setSelectedHostMediaPath] = useState("");

  const handleListenAddressChange = (event: ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;
    value = value.replace(/^:+/, "");
    value = value.replace(/\D/g, "");
    const formattedValue = value ? `:${value}` : ":";
    void formik.setFieldValue("listenAddress", formattedValue);
  };

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Server name:
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 600, ml: 2 }}>
          {formik.values.serverName || defaultServerName}
        </Typography>
      </Box>
      {!isDesktopShell && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title="Network address - application URL (open in your browser)">
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Listen address:
            </Typography>
          </Tooltip>
          <Tooltip title="Network address:port">
            <TextField
              label="address:port"
              name="listenAddress"
              value={
                formik.values.listenAddress.startsWith(":")
                  ? formik.values.listenAddress.slice(1)
                  : formik.values.listenAddress
              }
              onChange={handleListenAddressChange}
              onBlur={formik.handleBlur}
              margin="normal"
              size="small"
              error={Boolean(formik.touched.listenAddress && formik.errors.listenAddress)}
              helperText={(formik.touched.listenAddress && (formik.errors.listenAddress as string)) || " "}
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position="start">:</InputAdornment>,
                },
              }}
              sx={{ width: "30%", mb: 0 }}
            />
          </Tooltip>
        </Box>
      )}
      {!isDesktopShell && (
        <Box sx={{ mt: 0.5 }}>
          <Tooltip title="Allow cross-origin requests from browsers">
            <FormControlLabel
              control={
                <Switch
                  checked={formik.values.corsEnabled}
                  onChange={(_, checked) => formik.setFieldValue("corsEnabled", checked)}
                  name="corsEnabled"
                />
              }
              label="Enable CORS"
            />
          </Tooltip>
        </Box>
      )}
      <FormGroup row sx={{ width: "100%", justifyContent: "flex-start", gap: 2 }}>
        <Tooltip title="Enable authentication and authorization features">
          <FormControlLabel
            control={
              <Switch
                checked={formik.values.enableUserManagement}
                onChange={(_, checked) => formik.setFieldValue("enableUserManagement", checked)}
                name="enableUserManagement"
              />
            }
            label="User management"
          />
        </Tooltip>
        <Tooltip title="Permit new users to self-register">
          <FormControlLabel
            control={
              <Switch
                checked={formik.values.registrationOpen}
                onChange={(_, checked) => formik.setFieldValue("registrationOpen", checked)}
                name="registrationOpen"
                disabled={!formik.values.enableUserManagement}
              />
            }
            label="Allow self-registration"
          />
        </Tooltip>
      </FormGroup>
      <Box sx={{ width: "100%" }}>
        <Tooltip title="Detect MPEG-TS continuity counter and PCR discontinuity errors from streams">
          <FormControlLabel
            control={
              <Switch
                checked={formik.values.enableErrorDetection}
                onChange={(_, checked) => formik.setFieldValue("enableErrorDetection", checked)}
                name="enableErrorDetection"
              />
            }
            label="Stream error detection"
          />
        </Tooltip>
      </Box>
      <Box sx={{ width: "100%" }}>
        <Tooltip title="Collect lightweight system metrics for the System Monitor page">
          <FormControlLabel
            control={
              <Switch
                checked={formik.values.enableSystemMonitor}
                onChange={(_, checked) => formik.setFieldValue("enableSystemMonitor", checked)}
                name="enableSystemMonitor"
              />
            }
            label="System monitor"
          />
        </Tooltip>
      </Box>
      <Box sx={{ width: "100%" }}>
        <Tooltip title="Start active streams on load">
          <FormControlLabel
            control={
              <Switch
                checked={formik.values.autostartStreams}
                onChange={(_, checked) => formik.setFieldValue("autostartStreams", checked)}
                name="autostartStreams"
              />
            }
            label="Start active streams on load"
          />
        </Tooltip>
      </Box>
      {canRestartServer && (
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              System Monitor
            </Typography>
            <Tooltip title="View system monitor">
              <IconButton
                color="primary"
                size="small"
                onClick={() => navigate("/system-monitor")}
              >
                <BarChartIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Capabilities
            </Typography>
            <Tooltip title="View server capabilities">
              <IconButton
                color="primary"
                size="small"
                onClick={() => setCapabilitiesDialogOpen(true)}
              >
                <InfoOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Host media
            </Typography>
            <Tooltip title="Host media">
              <IconButton
                color="primary"
                size="small"
                onClick={() => setHostMediaDialogOpen(true)}
              >
                <FolderOpenIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {!isDesktopShell && (
              <>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Restart server
                </Typography>
                <Tooltip title="Restart the SkyView service">
                  <span>
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={onRequestRestart}
                      disabled={restartStatus.pending}
                    >
                      <RestartAltIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                {restartStatus.pending && <CircularProgress size={18} thickness={5} />}
              </>
            )}
          </Box>
          <CapabilitiesDialog
            open={capabilitiesDialogOpen}
            onClose={() => setCapabilitiesDialogOpen(false)}
          />
          <HostMediaFolder
            open={hostMediaDialogOpen}
            authorizedFetch={authorizedFetch}
            initialSelectedPath={selectedHostMediaPath}
            onClose={() => setHostMediaDialogOpen(false)}
            onSelect={(selection) => {
              setSelectedHostMediaPath(selection.fullPath);
              setHostMediaDialogOpen(false);
            }}
          />
          {restartStatus.error && (
            <Alert severity="error" sx={{ width: "100%", maxWidth: 420 }}>
              {restartStatus.error}
            </Alert>
          )}
        </>
      )}
    </>
  );
};

export default ServerForm;
